import { BaseService } from "@/services/BaseService";
import {
  MonitoringServer,
  CreateServerDto,
  UpdateServerDto,
  ServerHealth,
  CpuCurrent,
  MemoryCurrent,
  CpuHistoryPoint,
  MemoryHistoryPoint,
  MetricDataPoint,
} from "@/types/monitoring";

class MonitoringService extends BaseService {
  private basePath = "/admin/monitoring";

  // --- Servers ---

  async getServers(): Promise<MonitoringServer[]> {
    return this.get<MonitoringServer[]>(`${this.basePath}/servers`);
  }

  async getServer(id: string): Promise<MonitoringServer> {
    return this.get<MonitoringServer>(`${this.basePath}/servers/${id}`);
  }

  async createServer(data: CreateServerDto): Promise<MonitoringServer> {
    return this.post<MonitoringServer>(`${this.basePath}/servers`, data);
  }

  async updateServer(
    id: string,
    data: UpdateServerDto,
  ): Promise<MonitoringServer> {
    return this.patch<MonitoringServer>(`${this.basePath}/servers/${id}`, data);
  }

  async deleteServer(id: string): Promise<void> {
    return this.delete<void>(`${this.basePath}/servers/${id}`);
  }

  async getServerHealth(id: string): Promise<ServerHealth> {
    await this.get<unknown>(
      `${this.basePath}/servers/${id}/health`,
      undefined,
      { disableToast: true },
    );
    // Any successful response (2xx) means the server is reachable
    return { reachable: true };
  }

  // --- CPU ---

  async getCpuCurrent(serverId: string): Promise<CpuCurrent> {
    return this.get<CpuCurrent>(
      `${this.basePath}/servers/${serverId}/cpu/current`,
    );
  }

  async getCpuHistory(
    serverId: string,
    from: string,
    to: string,
  ): Promise<MetricDataPoint[]> {
    const raw = await this.get<CpuHistoryPoint[]>(
      `${this.basePath}/servers/${serverId}/cpu/history`,
      { from, to },
    );
    return raw.map((p) => ({
      timestamp: new Date(Number(p.time)).toISOString(),
      value: p.percent,
    }));
  }

  // --- Memory ---

  async getMemoryCurrent(serverId: string): Promise<MemoryCurrent> {
    return this.get<MemoryCurrent>(
      `${this.basePath}/servers/${serverId}/memory/current`,
    );
  }

  async getMemoryHistory(
    serverId: string,
    from: string,
    to: string,
  ): Promise<MetricDataPoint[]> {
    const raw = await this.get<MemoryHistoryPoint[]>(
      `${this.basePath}/servers/${serverId}/memory/history`,
      { from, to },
    );
    return raw.map((p) => ({
      timestamp: new Date(Number(p.time)).toISOString(),
      value: p.usedPercent,
    }));
  }

  // --- Containers ---

  async getContainerCpuHistory(
    serverId: string,
    containerId: string,
    from: string,
    to: string,
  ): Promise<MetricDataPoint[]> {
    const raw = await this.get<CpuHistoryPoint[]>(
      `${this.basePath}/servers/${serverId}/containers/${containerId}/cpu/history`,
      { from, to },
    );
    return raw.map((p) => ({
      timestamp: new Date(Number(p.time)).toISOString(),
      value: p.percent,
    }));
  }

  async getContainerMemoryHistory(
    serverId: string,
    containerId: string,
    from: string,
    to: string,
  ): Promise<MetricDataPoint[]> {
    const raw = await this.get<MemoryHistoryPoint[]>(
      `${this.basePath}/servers/${serverId}/containers/${containerId}/memory/history`,
      { from, to },
    );
    return raw.map((p) => ({
      timestamp: new Date(Number(p.time)).toISOString(),
      value: p.usedPercent,
    }));
  }
}

export const monitoringService = new MonitoringService();
